import _ from 'lodash';
import Mark from 'mark.js';
import {transparentize} from 'polished';
import React, {
  FC,
  PropsWithChildren,
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, {keyframes} from 'styled-components';

import {isElement, isNode} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {hasMetaKey} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  FrontMouseEvent,
  Handler,
  HandlerOf,
  MouseEventHandler,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ANNOTATION_JUMP_ANIMATION_DELAY_MS,
  ANNOTATION_JUMP_ANIMATION_DURATION_MS,
} from '../../../helpers/animationHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {
  clearCurrentSelection,
  getLocationFromCurrentSelection,
  isSelectionCollapsed,
  SelectionLocation,
} from '../../../helpers/domSelectionHelpers';
import {
  AnnotationEventTrackingSourcesEnum,
  canLocationAcceptMoreAnnotations,
  isSelectionInsideAnnotation,
} from '../../../helpers/models/annotationHelpers';
import {AnnotationHighlightCommentModel, AnnotationHighlightModel} from '../../../models/api/annotationModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {NewPanelId, RViewerPanelId, ViewerPanelId} from '../../../store/panels/panelStateBase';
import {AnnotationHighlightViewerToolbar} from './annotationHighlightViewerToolbar';
import {FloatingActivityDropdownContainer} from './floatingActivityDropdownContainer';

/*
 * Props.
 */

interface ActiveAnnotation {
  activityId: number;
  anchor: Anchor;
}

export interface AnnotationsHighlightViewerInheritableProps {
  panelId: NewPanelId;
  annotations?: ReadonlyArray<AnnotationHighlightCommentModel | AnnotationHighlightModel>;
  onQuoteInCommentClick?: HandlerOf<AnnotationHighlightCommentModel | AnnotationHighlightModel>;
  hasMessageAnnotations: boolean;
}

interface AnnotationsHighlightViewerProps extends AnnotationsHighlightViewerInheritableProps {
  conversationId?: number;
  namespace?: string;
  isAnnotatingComment?: boolean;
  onJumpToActivity?: HandlerOf<{panelId: ViewerPanelId; sourceActivityId: number}>;
  hasQuoteComment: boolean;
}

/*
 * Style.
 */

const annotationFocus = (p: ThemeContextType) => keyframes`
  from {
    background-color: ${transparentize(0.8, p.theme.palette.blue.shade40)};
  }
  to {
    background-color: ${transparentize(0.8, '#FF5C00')};
  }
`;

const StyledAnnotations = styled.div<{
  $isOverflowVisible: boolean | undefined;
}>`
  flex: 1 1 auto;
  overflow: ${(p) => (p.$isOverflowVisible ? 'visible' : 'auto')};
  display: flex;
  flex-direction: column;

  annotation.front_annotation {
    background-color: ${transparentize(0.8, '#FF5C00')};

    &[data-source-activity-id] {
      &.front_annotation_focused {
        background-color: ${(p) => transparentize(0.8, p.theme.palette.blue.shade40)};
        animation-name: ${annotationFocus};
        animation-delay: ${ANNOTATION_JUMP_ANIMATION_DELAY_MS}ms;
        animation-duration: ${ANNOTATION_JUMP_ANIMATION_DURATION_MS}ms;
        animation-timing-function: linear;
      }

      &.front_annotation_hover {
        cursor: pointer;
        background-color: ${transparentize(0.7, '#FF5C00')};
      }
    }
  }
`;

/*
 * Component.
 */

export const AnnotationsHighlightViewer: FC<PropsWithChildren<AnnotationsHighlightViewerProps>> = (props) => {
  const {
    annotations,
    hasMessageAnnotations,
    onQuoteInCommentClick,
    panelId,
    conversationId,
    namespace,
    isAnnotatingComment,
    hasQuoteComment,
    onJumpToActivity,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentLocation, setCurrentLocation] = useState<SelectionLocation | undefined>(undefined);
  const [activeAnnotation, setActiveAnnotation] = useState<ActiveAnnotation | undefined>(undefined);
  const shouldTrackActions = isInteger(conversationId) && namespace;

  const baseTrackingProps = useMemo(
    () => ({
      namespace,
      conversation_id: conversationId,
      message_source: isAnnotatingComment
        ? AnnotationEventTrackingSourcesEnum.COMMENT
        : AnnotationEventTrackingSourcesEnum.EMAIL,
    }),
    [conversationId, namespace, isAnnotatingComment],
  );

  const maybeTrackSelectedToComment = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_TEXT_HIGHLIGHT, baseTrackingProps);
  }, [baseTrackingProps, shouldTrackActions]);

  const maybeTrackClickOnCommentCta = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_POPOVER_CLICK, {
      ...baseTrackingProps,
      cta_type: 'comment',
    });
  }, [baseTrackingProps, shouldTrackActions]);

  const onSelectionChange = (selection: Selection | null, hasMetaKeyPressed: boolean) => {
    if (hasMetaKeyPressed || !hasMessageAnnotations) {
      setCurrentLocation(undefined);
      return false;
    }

    const location = getLocationFromCurrentSelection(selection, wrapperRef.current);

    if (!canLocationAcceptMoreAnnotations(location, annotations)) {
      setCurrentLocation(undefined);
      return false;
    }
    return true;
  };

  useOnSelectionChange({
    targetRef: wrapperRef,
    onSelectionChange,
    onSelectionDone: async (hasMetaKeyPressed) => {
      // Make sure we're not capturing an old selection (when unselecting, we're sometimes getting the previous (non-empty) selection)
      await delayAsync(0);

      const selection = wrapperRef.current?.ownerDocument?.getSelection();

      if (!selection || !onSelectionChange(selection, hasMetaKeyPressed)) {
        return;
      }

      const location = getLocationFromCurrentSelection(selection, wrapperRef.current);

      // Only track when we have a selection.
      if (location) {
        maybeTrackSelectedToComment();
      }

      setCurrentLocation(location);
    },
  });

  useOnClickOutsideTarget(wrapperRef, () => {
    setCurrentLocation(undefined);
    setActiveAnnotation(undefined);
  });

  const onAnnotationClick = useCallback(
    (event: FrontMouseEvent) => {
      const elementTarget = isElement(event.target) && event.target;
      if (!elementTarget) {
        setActiveAnnotation(undefined);
        return;
      }

      const isTargetALink = elementTarget.tagName === 'A' || elementTarget.closest('a');

      if (isTargetALink) {
        setActiveAnnotation(undefined);
        return;
      }

      // Hold meta to disable annotation UI.
      // If the preview gets in the way when you try to select something.
      if (hasMetaKey(event)) {
        setActiveAnnotation(undefined);
        return;
      }

      if (!isSelectionCollapsed(wrapperRef.current)) {
        setActiveAnnotation(undefined);
        return;
      }

      // Grab the id of the parent of the target.
      const parentSourceActivityId = Number(findSourceActivityId(elementTarget.parentElement));

      // Grab the id of the next sibling of the parent of the target.
      const siblingSourceActivityId = Number(
        findSourceActivityId(elementTarget.parentElement?.nextElementSibling),
      );

      // Grab the id of the target.
      const targetSourceActivityId = Number(findSourceActivityId(elementTarget));
      const targetIsInsideAnotherAnnotation =
        Number.isFinite(targetSourceActivityId) &&
        Number.isFinite(parentSourceActivityId) &&
        targetSourceActivityId !== parentSourceActivityId;
      const targetIsTheSameAsSibling =
        Number.isFinite(targetSourceActivityId) &&
        Number.isFinite(siblingSourceActivityId) &&
        targetSourceActivityId === siblingSourceActivityId;

      const sourceActivityId =
        targetIsInsideAnotherAnnotation && targetIsTheSameAsSibling
          ? parentSourceActivityId
          : targetSourceActivityId;

      if (!Number.isFinite(sourceActivityId)) {
        setActiveAnnotation(undefined);
        return;
      }

      setActiveAnnotation((prevAnnotation) => {
        if (prevAnnotation) {
          return undefined;
        }

        return {
          activityId: sourceActivityId,
          // TODO: Avoid using the ! operator.
          anchor: () =>
            wrapperRef
              .current!.querySelector(`[data-source-activity-id="${sourceActivityId}"]`)!
              .getBoundingClientRect(),
        };
      });

      if (isAnnotatingComment && onJumpToActivity && RViewerPanelId.is(panelId)) {
        onJumpToActivity({panelId, sourceActivityId});
      }
    },
    [setActiveAnnotation, isAnnotatingComment, panelId, onJumpToActivity],
  );

  const onAnnotationMouseOver: HandlerOf<Event> = useCallback(
    (e) => {
      if (!isElement(e.target) || !hasMessageAnnotations) {
        return;
      }

      const activityId = findSourceActivityId(e.target);

      if (!activityId) {
        return;
      }

      const contentWrapper = wrapperRef.current;

      if (!contentWrapper) {
        return;
      }

      const annotationElements = Array.from(
        contentWrapper.querySelectorAll(`[data-source-activity-id="${activityId}"]`),
      );

      if (onQuoteInCommentClick !== undefined) {
        annotationElements.forEach((el) => {
          el.classList.add('front_annotation_hover');
        });
      }
    },
    [hasMessageAnnotations, onQuoteInCommentClick],
  );
  const onAnnotationMouseLeave: HandlerOf<Event> = useCallback(
    (e) => {
      if (!isElement(e.target) || !hasMessageAnnotations) {
        return;
      }

      const activityId = findSourceActivityId(e.target);

      if (!activityId) {
        return;
      }

      const contentWrapper = wrapperRef.current;

      if (!contentWrapper) {
        return;
      }

      const annotationElements = Array.from(
        contentWrapper.querySelectorAll(`[data-source-activity-id="${activityId}"]`),
      );

      annotationElements.forEach((el) => {
        el.classList.remove('front_annotation_hover');
      });
    },
    [hasMessageAnnotations],
  );

  useLayoutEffect(
    () => {
      const contentWrapper = wrapperRef.current;

      if (!hasMessageAnnotations || !contentWrapper || !annotations || annotations.length === 0) {
        return () => {};
      }

      const location = getLocationFromCurrentSelection(
        contentWrapper.ownerDocument?.getSelection(),
        contentWrapper,
      );

      const annotationsAlreadyMarked = _(contentWrapper.querySelectorAll('[data-source-activity-id]'))
        .map((el) => el.getAttribute('data-source-activity-id'))
        .uniq()
        .value().length;

      if (annotations.length === annotationsAlreadyMarked) {
        return () => {};
      }

      // If we have a selection, stop here to avoid removing it.
      if (
        isSelectionInsideAnnotation(location, annotations) ||
        contentWrapper.querySelector('.front_annotation_focused')
      ) {
        return () => {};
      }

      const mark = new Mark(contentWrapper);

      annotations.forEach((annotation) => {
        const markRange = {
          start: annotation.startIndex,
          length: annotation.endIndex - annotation.startIndex,
        };

        mark.markRanges([markRange], {
          className: 'front_annotation',
          element: 'annotation',
          each: (node) => {
            // Comment drafts can have annotations, but don't have an activity yet.
            if (annotation.commentActivityId) {
              node.setAttribute('data-source-activity-id', annotation.commentActivityId.toString());
              // This helps alleviate the issue where a highlight is broken up into multiple nodes
              node.addEventListener('mouseover', onAnnotationMouseOver);
              node.addEventListener('mouseleave', onAnnotationMouseLeave);
            }
          },
        });
      });

      return () => {
        const locationFromCurrentSelection = getLocationFromCurrentSelection(
          contentWrapper.ownerDocument?.getSelection(),
          contentWrapper,
        );

        // If we have a selection, stop here to avoid removing it.
        if (isSelectionInsideAnnotation(locationFromCurrentSelection, annotations)) {
          return;
        }

        const annotationElements = Array.from(contentWrapper.querySelectorAll(`[data-source-activity-id]`));
        annotationElements.forEach((el) => {
          el.removeEventListener('mouseover', onAnnotationMouseOver);
          el.removeEventListener('mouseleave', onAnnotationMouseLeave);
        });
        mark.unmark();
      };
    },
    hasQuoteComment
      ? undefined
      : [annotations, hasMessageAnnotations, onAnnotationMouseLeave, onAnnotationMouseOver],
  );

  const maybeRenderToolbar = () => {
    if (!currentLocation) {
      return null;
    }

    const onRequestClose = () => {
      setCurrentLocation(undefined);
    };

    const onQuoteInComment = (annotation: AnnotationHighlightModel | AnnotationHighlightCommentModel) => {
      if (onQuoteInCommentClick) {
        maybeTrackClickOnCommentCta();
        onQuoteInCommentClick(annotation);
      }

      clearCurrentSelection(wrapperRef.current);
    };

    return (
      <AnnotationHighlightViewerToolbar
        currentLocation={currentLocation}
        onQuoteInCommentClick={onQuoteInComment}
        onRequestClose={onRequestClose}
        isAnnotatingComment={isAnnotatingComment}
      />
    );
  };

  const maybeRenderActiveAnnotation = () => {
    if (!activeAnnotation || isAnnotatingComment) {
      return null;
    }

    const onRequestClose = () => {
      setActiveAnnotation(undefined);
    };

    return (
      <FloatingActivityDropdownContainer
        panelId={panelId}
        activityId={activeAnnotation.activityId}
        anchor={activeAnnotation.anchor}
        placement="bottom"
        onRequestClose={onRequestClose}
      />
    );
  };

  if (!hasMessageAnnotations) {
    return (
      <StyledAnnotations ref={wrapperRef} $isOverflowVisible={props.isAnnotatingComment}>
        {props.children}
      </StyledAnnotations>
    );
  }

  return (
    <StyledAnnotations
      ref={wrapperRef}
      $isOverflowVisible={props.isAnnotatingComment}
      onClick={onAnnotationClick}
    >
      {props.children}
      {maybeRenderToolbar()}
      {maybeRenderActiveAnnotation()}
    </StyledAnnotations>
  );
};

function useOnClickOutsideTarget(targetRef: RefObject<HTMLElement | null>, listener: Handler) {
  // Keep a ref to the latest version of the listener.
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useInitialEffect(() => {
    const target = targetRef.current;
    if (!target) {
      throw new Error('A valid ref should be provided.');
    }

    const {body} = target.ownerDocument;
    if (!body) {
      throw new Error('A document could not be found for the ref.');
    }

    // Wrap the call to the listener ref into a stable handler.
    const stableListener: MouseEventHandler = (e) => {
      if (!isNode(e.target)) {
        return;
      }

      if (target.contains(e.target)) {
        return;
      }

      listenerRef.current();
    };
    body.addEventListener('click', stableListener, true);

    return () => {
      body.removeEventListener('click', stableListener, true);
    };
  });
}

interface UseOnSelectionChangeOptions {
  targetRef: RefObject<HTMLElement | null>;
  onSelectionChange: (sel: Selection | null, hasMetaKey: boolean) => void;
  onSelectionDone: (hasMetaKey: boolean) => void;
}
function useOnSelectionChange({targetRef, onSelectionChange, onSelectionDone}: UseOnSelectionChangeOptions) {
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const onSelectionDoneRef = useRef(onSelectionDone);
  onSelectionDoneRef.current = onSelectionDone;
  const metaKeyPressed = useRef(false);
  const selection = useRef<Selection | null>(null);

  useInitialEffect(() => {
    const target = targetRef.current;
    if (!target) {
      throw new Error('A valid ref should be provided.');
    }

    const doc = target.ownerDocument;
    if (!doc) {
      throw new Error('A document could not be found for the ref.');
    }

    // Wrap the call to the listener ref into a stable handler.
    const stableListener: EventListener = () => {
      selection.current = doc.getSelection();
      onSelectionChangeRef.current(selection.current, metaKeyPressed.current);
    };
    const onMouse = (e: MouseEvent) => {
      metaKeyPressed.current = e.metaKey;
    };
    const onMouseUp = (e: MouseEvent) => {
      metaKeyPressed.current = e.metaKey;

      onSelectionDoneRef.current(e.metaKey);
    };
    doc.addEventListener('selectionchange', stableListener);
    doc.addEventListener('mousedown', onMouse, true);
    doc.addEventListener('mouseup', onMouseUp, true);

    return () => {
      doc.removeEventListener('selectionchange', stableListener);
      doc.removeEventListener('mousedown', onMouse, true);
      doc.removeEventListener('mouseup', onMouseUp, true);
    };
  });
}

function findSourceActivityId(element: Element | undefined | null) {
  return element?.getAttribute('data-source-activity-id') ?? undefined;
}
